import { zodResolver } from '@hookform/resolvers/zod';
import { Box, MenuItem, TextField } from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { RECORD_STATUSES, carrierInputSchema } from '@app/shared';
import type { CarrierInput } from '@app/shared';
import { useNotify } from '../../components/NotifyProvider';
import { FormDialog } from '../../components/dialogs';
import { SelectInput, TextInput, applyServerErrors, toOptions } from '../../components/form';
import { useCreateCarrier, useUpdateCarrier } from './api';
import { useCarriers } from './lookups';

const NEW = '';
const BLANK: CarrierInput = { code: '', name: '', status: 'Active' };

/**
 * "Carrier Setup": add a carrier or edit an existing one.
 * Assumption: the reference's dialog was not observed; code, name and status are the minimum
 * the rest of the screen needs.
 */
export function CarrierSetupDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const notify = useNotify();
  const carriers = useCarriers(open);
  const createCarrier = useCreateCarrier();
  const updateCarrier = useUpdateCarrier();
  /** Id of the carrier being edited, or `NEW`. */
  const [selected, setSelected] = useState(NEW);

  const { control, handleSubmit, reset, setError } = useForm<CarrierInput>({
    resolver: zodResolver(carrierInputSchema),
    defaultValues: BLANK,
  });

  const choose = (id: string) => {
    setSelected(id);
    const carrier = carriers.data?.find((c) => c.id === id);
    reset(carrier ? { code: carrier.code, name: carrier.name, status: carrier.status } : BLANK);
  };

  // Always opens on "add", whatever was selected last time.
  useEffect(() => {
    if (!open) return;
    setSelected(NEW);
    reset(BLANK);
  }, [open, reset]);

  const submit = handleSubmit(async (values) => {
    try {
      if (selected === NEW) {
        const created = await createCarrier.mutateAsync(values);
        notify.success(`Carrier ${created.code} created.`);
      } else {
        const updated = await updateCarrier.mutateAsync({ id: selected, input: values });
        notify.success(`Carrier ${updated.code} updated.`);
      }
      onClose();
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });

  return (
    <FormDialog
      open={open}
      title="Carrier Setup"
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      submitting={createCarrier.isPending || updateCarrier.isPending}
      maxWidth="xs"
    >
      <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
        <TextField select label="Carrier" value={selected} onChange={(e) => choose(e.target.value)}>
          <MenuItem value={NEW}>
            <em>Add new carrier</em>
          </MenuItem>
          {(carriers.data ?? []).map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.code} ({c.name})
            </MenuItem>
          ))}
        </TextField>
        <TextInput
          control={control}
          name="code"
          label="Carrier Code"
          required
          helperText="Stored in upper case"
          slotProps={{ htmlInput: { maxLength: 20 } }}
        />
        <TextInput control={control} name="name" label="Carrier Name" required />
        <SelectInput
          control={control}
          name="status"
          label="Status"
          options={toOptions(RECORD_STATUSES)}
        />
      </Box>
    </FormDialog>
  );
}
